import { worldToRadar, type Level, type MapId, type RadarPoint } from "@69ly/maps";
import type { Side } from "@69ly/shared";
import { useTranslation } from "react-i18next";
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type MouseEvent, type PointerEvent, type ReactNode } from "react";
import { radarUrl } from "../lib/radars.ts";
import { CalloutLayer, setCalloutsShown, useCalloutsShown } from "./CalloutLayer.tsx";
import { Icon } from "./Icon.tsx";

export const pct = (n: number) => `${(n * 100).toFixed(3)}%`;

/** Places a child at a world position on the radar. */
export function OnMap({ map, x, y, children, className }: { map: MapId; x: number; y: number; children: ReactNode; className?: string }) {
  const p = worldToRadar(map, x, y);
  return <AtPoint u={p.u} v={p.v} children={children} className={className} />;
}

/** Places a child at a radar point (fractions of its size). */
export function AtPoint({ u, v, children, className }: { u: number; v: number; children: ReactNode; className?: string }) {
  return (
    <div className={`on-map ${className ?? ""}`} style={{ left: pct(u), top: pct(v) }}>
      {children}
    </div>
  );
}

/** A straight line between two world points: the throw. */
export function ThrowLine({ map, from, to, side, className }: { map: MapId; from: { x: number; y: number }; to: { x: number; y: number }; side?: Side; className?: string }) {
  const a = worldToRadar(map, from.x, from.y);
  const b = worldToRadar(map, to.x, to.y);
  return (
    <svg className={`throw-line${side ? ` side-${side}` : ""}${className ? ` ${className}` : ""}`} viewBox="0 0 1000 1000" preserveAspectRatio="none" aria-hidden="true">
      <line x1={a.u * 1000} y1={a.v * 1000} x2={b.u * 1000} y2={b.v * 1000} />
    </svg>
  );
}

/** A view of the radar: its centre (radar fractions) and how much bigger than the whole map. */
export interface RadarFocus {
  u: number;
  v: number;
  scale: number;
}

const WHOLE: RadarFocus = { u: 0.5, v: 0.5, scale: 1 };
export const MAX_ZOOM = 6;

/** How far past its edges the radar may be moved, in fractions of the frame (none: edge to edge). */
export interface Overscroll {
  x: number;
  y: number;
}
const NO_OVERSCROLL: Overscroll = { x: 0, y: 0 };

/** Keeps the radar edge to edge: the centre cannot get closer to a border than half a view, less the overscroll. */
function clampView(view: RadarFocus, over: Overscroll = NO_OVERSCROLL): RadarFocus {
  const scale = Math.min(MAX_ZOOM, Math.max(1, view.scale));
  const c = (x: number, o: number) => Math.min(1 - (0.5 - o) / scale, Math.max((0.5 - o) / scale, x));
  return { u: c(view.u, over.x), v: c(view.v, over.y), scale };
}

/** Zooms to `scale` keeping the radar point under `at` (fractions of the frame) where it is. */
export function zoomAround(view: RadarFocus, at: { x: number; y: number }, scale: number, over: Overscroll = NO_OVERSCROLL): RadarFocus {
  const p = { u: view.u + (at.x - 0.5) / view.scale, v: view.v + (at.y - 0.5) / view.scale };
  const s = Math.min(MAX_ZOOM, Math.max(1, scale));
  return clampView({ u: p.u - (at.x - 0.5) / s, v: p.v - (at.y - 0.5) / s, scale: s }, over);
}

/** The zoom that fits these world points with some room around them (no zoom for none or one far apart). */
export function focusOn(map: MapId, points: { x: number; y: number }[], { pad = 0.06, maxScale = 3 } = {}): RadarFocus | undefined {
  if (points.length === 0) return undefined;
  const ps = points.map((p) => worldToRadar(map, p.x, p.y));
  return focusOnBox({ u0: Math.min(...ps.map((p) => p.u)), u1: Math.max(...ps.map((p) => p.u)), v0: Math.min(...ps.map((p) => p.v)), v1: Math.max(...ps.map((p) => p.v)) }, { pad, maxScale });
}

/** The zoom that fits a box of the radar. */
export function focusOnBox(b: { u0: number; v0: number; u1: number; v1: number }, { pad = 0.06, maxScale = 3 } = {}): RadarFocus | undefined {
  const size = Math.max(b.u1 - b.u0, b.v1 - b.v0, 0.12) + 2 * pad;
  const scale = Math.min(maxScale, Math.max(1, 1 / size));
  if (scale <= 1.05) return undefined;
  return { u: (b.u0 + b.u1) / 2, v: (b.v0 + b.v1) / 2, scale };
}

interface RadarContextValue {
  /** How much the radar is zoomed in. */
  scale: number;
  /** Quarter turns clockwise (0–3) the radar is shown at. */
  turn: Turn;
  /** The angle it is drawn at right now, in degrees clockwise (eased between quarter turns). */
  angle: number;
  /** The frame's width in CSS pixels (0 before it is measured). */
  width: number;
  /** A radar point to pixels from the frame's top-left corner (turned with the map). */
  toScreen: (u: number, v: number) => { x: number; y: number };
  /** The same before the turn: for drawing a whole picture under a canvas rotated by `turn`. */
  toFrame: (u: number, v: number) => { x: number; y: number };
  /** An unzoomed layer over the radar, for popovers. */
  overlay: HTMLDivElement | null;
  /**
   * Where canvases draw: the overlay, or with `bleedInto` a layer as large as the area the
   * radar spills into, `bleed` pixels past each side of the square (toScreen stays square-based).
   */
  canvasLayer: HTMLDivElement | null;
  bleed: Bleed;
}

export interface Bleed {
  left: number;
  top: number;
  right: number;
  bottom: number;
}
const NO_BLEED: Bleed = { left: 0, top: 0, right: 0, bottom: 0 };

const RadarContext = createContext<RadarContextValue>({
  scale: 1,
  turn: 0,
  angle: 0,
  width: 0,
  toScreen: () => ({ x: 0, y: 0 }),
  toFrame: () => ({ x: 0, y: 0 }),
  overlay: null,
  canvasLayer: null,
  bleed: NO_BLEED,
});

export type Turn = 0 | 1 | 2 | 3;
/** A point of the frame (fractions) turned `deg` degrees clockwise around its middle. */
export function rotatePoint(deg: number, x: number, y: number): { x: number; y: number } {
  const a = (deg * Math.PI) / 180;
  const c = Math.cos(a);
  const s = Math.sin(a);
  const dx = x - 0.5;
  const dy = y - 0.5;
  return { x: 0.5 + dx * c - dy * s, y: 0.5 + dx * s + dy * c };
}
/** A point of the unturned frame (fractions) where it shows once turned `k` quarter turns clockwise. */
export function turnPoint(k: Turn, x: number, y: number): { x: number; y: number } {
  return k === 1 ? { x: 1 - y, y: x } : k === 2 ? { x: 1 - x, y: 1 - y } : k === 3 ? { x: y, y: 1 - x } : { x, y };
}
const reducedMotion = () => !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
const easeOut = (k: number) => 1 - Math.pow(1 - k, 3);
const easeInOut = (k: number) => (k < 0.5 ? 4 * k * k * k : 1 - Math.pow(-2 * k + 2, 3) / 2);
/** Back: a point on screen (fractions of the frame) to the unturned frame. */
export function unturnPoint(k: Turn, x: number, y: number): { x: number; y: number } {
  return turnPoint(((4 - k) % 4) as Turn, x, y);
}
const unturnVector = (k: Turn, dx: number, dy: number) => (k === 1 ? { dx: dy, dy: -dx } : k === 2 ? { dx: -dx, dy: -dy } : k === 3 ? { dx: -dy, dy: dx } : { dx, dy });

/** How a map is turned, kept per map in this browser (the same on every page). */
function useMapTurn(map: MapId): [Turn, () => void] {
  const key = `69ly.radarTurn.${map}`;
  const read = (): Turn => {
    try {
      const n = Number(localStorage.getItem(key));
      return n === 1 || n === 2 || n === 3 ? n : 0;
    } catch {
      return 0;
    }
  };
  const [turn, setTurn] = useState<Turn>(read);
  useEffect(() => setTurn(read()), [key]); // eslint-disable-line react-hooks/exhaustive-deps
  const next = () =>
    setTurn((k) => {
      const n = ((k + 1) % 4) as Turn;
      try {
        localStorage.setItem(key, String(n));
      } catch {
        // Storage off: the turn lasts for this page.
      }
      return n;
    });
  return [turn, next];
}

/** The radar around this component: zoom, size, and where points land on screen. */
export const useRadarView = () => useContext(RadarContext);

interface RadarProps {
  map: MapId;
  mapName: string;
  level: Level;
  children?: ReactNode;
  /** Called with the radar point under a click, for picking a landing spot. */
  onPick?: (p: RadarPoint) => void;
  /** Called on a click on the map itself, not on a marker: unselect. */
  onBackground?: () => void;
  dim?: boolean;
  /** Where the view starts, and goes back to with the fit button. Markers keep their size when zoomed. */
  focus?: RadarFocus;
  /** Zoom buttons; the wheel, dragging and pinching work either way. */
  controls?: boolean;
  /** Callout names and their button; off where the page draws its own (callout admin). */
  callouts?: boolean;
  /** A button turns the map a quarter turn at a time (pages that place things by their own pointer maths leave it off). */
  turnable?: boolean;
  /**
   * A selector of an ancestor wider than the square (the demo viewer's stage): zoomed in,
   * the map and what is drawn on it spill into that whole area instead of stopping at the square.
   */
  bleedInto?: string;
  /**
   * Pixels the map may be moved past its edges (zoomed or not), so what lies at an edge can
   * come out from under what the page lays over it (the demo viewer's panels and timeline).
   */
  overscroll?: { x: number; y: number };
}

const DRAG_THRESHOLD = 5;

/** Valve's radar, square, with overlays positioned in fractions of its size. Zoom with the buttons, the wheel or a pinch; drag to move. */
export function Radar({ map, mapName, level, children, onPick, onBackground, dim, focus, controls = true, callouts: withCallouts = true, turnable = false, bleedInto, overscroll }: RadarProps) {
  const { t } = useTranslation();
  const [savedTurn, nextTurn] = useMapTurn(map);
  const turn: Turn = turnable ? savedTurn : 0;
  const turnRef = useRef(turn);
  turnRef.current = turn;
  // The drawn angle eases to the turn: a quarter turn clockwise each time.
  const [angle, setAngle] = useState(turn * 90);
  const angleRef = useRef(turn * 90);
  const spin = useRef(0);
  useEffect(() => {
    const goal = angleRef.current + ((((turn * 90 - angleRef.current) % 360) + 360) % 360);
    const from = angleRef.current;
    cancelAnimationFrame(spin.current);
    // Opened turned, or no motion wanted: at once.
    if (goal - from > 90 || reducedMotion()) {
      angleRef.current = turn * 90;
      setAngle(turn * 90);
      return;
    }
    const t0 = performance.now();
    const step = (now: number) => {
      const k = Math.min(1, (now - t0) / 380);
      angleRef.current = from + (goal - from) * easeInOut(k);
      setAngle(angleRef.current);
      if (k < 1) spin.current = requestAnimationFrame(step);
    };
    spin.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(spin.current);
  }, [turn]);
  const frame = useRef<HTMLDivElement>(null);
  const [overlay, setOverlay] = useState<HTMLDivElement | null>(null);
  const [bleedLayer, setBleedLayer] = useState<HTMLDivElement | null>(null);
  const [bleed, setBleed] = useState<Bleed>(NO_BLEED);
  const [width, setWidth] = useState(0);
  // The overscroll in fractions of the frame (a square: its width), at most a third of it.
  const over: Overscroll = useMemo(
    () => (overscroll && width ? { x: Math.min(1 / 3, overscroll.x / width), y: Math.min(1 / 3, overscroll.y / width) } : NO_OVERSCROLL),
    [overscroll?.x, overscroll?.y, width], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const overRef = useRef(over);
  overRef.current = over;
  const canMove = over.x > 0 || over.y > 0;
  const [moving, setMoving] = useState(false);
  const callouts = useCalloutsShown() && withCallouts;

  const target = focus && focus.scale > 1 ? clampView(focus) : WHOLE;
  const targetKey = `${target.u.toFixed(4)} ${target.v.toFixed(4)} ${target.scale.toFixed(3)}`;
  const [view, setViewNow] = useState<RadarFocus>(target);
  const viewRef = useRef(view);
  useEffect(() => {
    viewRef.current = view;
  }, [view]);
  // Moves glide (buttons, a new focus); the wheel, drags and pinches follow the hand at once.
  const glide = useRef(0);
  const setView = useCallback((next: RadarFocus | ((v: RadarFocus) => RadarFocus)) => {
    cancelAnimationFrame(glide.current);
    setViewNow(next);
  }, []);
  const glideTo = useCallback((to: RadarFocus) => {
    cancelAnimationFrame(glide.current);
    const from = viewRef.current;
    if (reducedMotion()) {
      viewRef.current = to;
      setViewNow(to);
      return;
    }
    const t0 = performance.now();
    const step = (now: number) => {
      const k = easeOut(Math.min(1, (now - t0) / 320));
      const v = { u: from.u + (to.u - from.u) * k, v: from.v + (to.v - from.v) * k, scale: from.scale + (to.scale - from.scale) * k };
      viewRef.current = v;
      setViewNow(v);
      if (k < 1) glide.current = requestAnimationFrame(step);
    };
    glide.current = requestAnimationFrame(step);
  }, []);
  // A new focus (another site, another selection, the players' area) moves the view there.
  const [seenKey, setSeenKey] = useState(targetKey);
  useEffect(() => {
    if (seenKey === targetKey) return;
    setSeenKey(targetKey);
    glideTo(target);
  }, [targetKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    const el = frame.current;
    if (!el) return;
    const area = bleedInto ? el.parentElement?.closest<HTMLElement>(bleedInto) : null;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setWidth(r.width);
      if (!area) return;
      const a = area.getBoundingClientRect();
      const next = { left: Math.max(0, r.left - a.left), top: Math.max(0, r.top - a.top), right: Math.max(0, a.right - r.right), bottom: Math.max(0, a.bottom - r.bottom) };
      setBleed((b) => (b.left === next.left && b.top === next.top && b.right === next.right && b.bottom === next.bottom ? b : next));
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    if (area) ro.observe(area);
    return () => ro.disconnect();
  }, [bleedInto]);

  // The wheel zooms around the pointer. Listened to by hand: React's wheel listener cannot stop the page scrolling.
  useEffect(() => {
    const el = frame.current;
    if (!el) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onWheel = (e: WheelEvent) => {
      const k = Math.exp(-e.deltaY * (e.deltaMode === 1 ? 0.05 : 0.002));
      const v = viewRef.current;
      // Nothing to zoom out of: let the page scroll.
      if ((k < 1 && v.scale <= 1) || (k > 1 && v.scale >= MAX_ZOOM)) return;
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const next = zoomAround(v, unturnPoint(turnRef.current, (e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height), v.scale * k, overRef.current);
      viewRef.current = next;
      setView(next);
      setMoving(true);
      clearTimeout(timer);
      timer = setTimeout(() => setMoving(false), 160);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      el.removeEventListener("wheel", onWheel);
      clearTimeout(timer);
    };
  }, [setView]);

  // Dragging moves a zoomed map; two fingers zoom. A drag is not a click.
  const gesture = useRef({ pointers: new Map<number, { x: number; y: number }>(), start: { x: 0, y: 0 }, moved: false, suppressClick: false });
  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    const g = gesture.current;
    g.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (g.pointers.size === 1) {
      g.start = { x: e.clientX, y: e.clientY };
      g.moved = false;
    }
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const g = gesture.current;
    const prev = g.pointers.get(e.pointerId);
    const el = frame.current;
    if (!prev || !el) return;
    const all = [...g.pointers.entries()];
    g.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const r = el.getBoundingClientRect();
    if (all.length >= 2) {
      // Pinch: the distance between the first two fingers sets the zoom, their middle stays put.
      const [a, b] = all.map(([id, p]) => (id === e.pointerId ? { x: e.clientX, y: e.clientY } : p));
      const [pa, pb] = all.map(([, p]) => p);
      const before = Math.hypot(pa.x - pb.x, pa.y - pb.y);
      const after = Math.hypot(a.x - b.x, a.y - b.y);
      if (before < 1) return;
      const mid = unturnPoint(turnRef.current, ((a.x + b.x) / 2 - r.left) / r.width, ((a.y + b.y) / 2 - r.top) / r.height);
      g.moved = true;
      setMoving(true);
      setView((v) => zoomAround(v, mid, (v.scale * after) / before, overRef.current));
      return;
    }
    if (!g.moved) {
      if ((viewRef.current.scale <= 1 && !canMove) || Math.hypot(e.clientX - g.start.x, e.clientY - g.start.y) < DRAG_THRESHOLD) return;
      g.moved = true;
      setMoving(true);
      el.setPointerCapture?.(e.pointerId);
    }
    const { dx, dy } = unturnVector(turnRef.current, (e.clientX - prev.x) / r.width, (e.clientY - prev.y) / r.height);
    setView((v) => clampView({ u: v.u - dx / v.scale, v: v.v - dy / v.scale, scale: v.scale }, overRef.current));
  };
  const onPointerEnd = (e: PointerEvent<HTMLDivElement>) => {
    const g = gesture.current;
    g.pointers.delete(e.pointerId);
    if (g.pointers.size === 0 && g.moved) {
      g.suppressClick = true;
      setTimeout(() => {
        g.suppressClick = false;
      }, 0);
      setMoving(false);
    }
  };
  const onClickCapture = (e: MouseEvent<HTMLDivElement>) => {
    if (gesture.current.suppressClick) {
      e.stopPropagation();
      e.preventDefault();
      gesture.current.suppressClick = false;
    }
  };

  const pick = (e: MouseEvent<HTMLDivElement>) => {
    if (onPick) {
      // The zoomed layer's box: a click maps back to the whole radar.
      const rect = e.currentTarget.getBoundingClientRect();
      const p = unturnPoint(turn, (e.clientX - rect.left) / rect.width, (e.clientY - rect.top) / rect.height);
      onPick({ u: p.x, v: p.y });
    } else if (onBackground && !(e.target as HTMLElement).closest("button, a")) {
      onBackground();
    }
  };

  const s = view.scale;
  const zoomed = s > 1.001;
  // Moved past its edges at full size (overscroll): placed the same way.
  const placed = zoomed || Math.abs(view.u - 0.5) > 0.0005 || Math.abs(view.v - 0.5) > 0.0005;
  const style: CSSProperties | undefined = placed ? { transform: `translate(${(0.5 - view.u * s) * 100}%, ${(0.5 - view.v * s) * 100}%) scale(${s})`, ["--zoom" as string]: s } : undefined;
  const ctx = useMemo<RadarContextValue>(
    () => ({
      scale: s,
      turn,
      angle,
      width,
      toScreen: (u, v) => {
        const p = rotatePoint(angle, (u - view.u) * s + 0.5, (v - view.v) * s + 0.5);
        return { x: p.x * width, y: p.y * width };
      },
      toFrame: (u, v) => ({ x: ((u - view.u) * s + 0.5) * width, y: ((v - view.v) * s + 0.5) * width }),
      overlay,
      canvasLayer: bleedInto ? bleedLayer : overlay,
      bleed: bleedInto ? bleed : NO_BLEED,
    }),
    [s, turn, angle, view.u, view.v, width, overlay, bleedInto, bleedLayer, bleed],
  );
  const atTarget = Math.abs(view.scale - target.scale) < 0.01 && Math.abs(view.u - target.u) < 0.002 && Math.abs(view.v - target.v) < 0.002;
  const zoomBy = (k: number) => glideTo(zoomAround(viewRef.current, { x: 0.5, y: 0.5 }, viewRef.current.scale * k, overRef.current));

  return (
    <div
      ref={frame}
      className={`radar${bleedInto ? " is-bleeding" : ""}${turn ? " is-turned" : ""}${onPick ? " is-picking" : ""}${dim ? " is-dim" : ""}${zoomed || canMove ? " is-zoomed-in" : ""}${moving ? " is-moving" : ""}`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      onClickCapture={onClickCapture}
    >
      <RadarContext.Provider value={ctx}>
        <div className="radar-turn" style={{ ["--turn" as string]: `${angle}deg` }}>
          <div className={`radar-layer${zoomed ? " is-zoomed" : ""}`} style={style} onClick={pick}>
            <img src={radarUrl(map, level)} alt={t("mapView.radarAlt", { map: mapName })} draggable={false} />
            {callouts && <CalloutLayer map={map} level={level} scale={s} width={width} />}
            {children}
          </div>
        </div>
        {bleedInto && <div className="radar-bleed" ref={setBleedLayer} style={{ left: -bleed.left, top: -bleed.top, right: -bleed.right, bottom: -bleed.bottom }} />}
        <div className="radar-overlay" ref={setOverlay} />
      </RadarContext.Provider>
      {controls && (
        <div
          className="radar-zoom glass"
          role="group"
          aria-label={t("radar.zoom")}
          // Spilling: beside the area's right edge (less what the page keeps there), not the square's.
          style={bleedInto ? { right: `calc(var(--radar-controls-right, var(--space-3)) - ${bleed.right}px)` } : undefined}
        >
          <button type="button" className="radar-zoom-btn" aria-label={t("radar.zoomIn")} title={t("radar.zoomIn")} disabled={s >= MAX_ZOOM} onClick={() => zoomBy(1.6)}>
            <Icon name="plus" />
          </button>
          <button type="button" className="radar-zoom-btn" aria-label={t("radar.zoomOut")} title={t("radar.zoomOut")} disabled={!zoomed} onClick={() => zoomBy(1 / 1.6)}>
            <Icon name="minus" />
          </button>
          {withCallouts && (
            <button type="button" className="radar-zoom-btn" aria-label={t("radar.callouts")} title={t("radar.callouts")} aria-pressed={callouts} onClick={() => setCalloutsShown(!callouts)}>
              <Icon name="tag" />
            </button>
          )}
          {turnable && (
            <button type="button" className="radar-zoom-btn" aria-label={t("radar.turn")} title={t("radar.turnHelp", { deg: turn * 90 })} onClick={nextTurn}>
              <Icon name="rotate" />
            </button>
          )}
          <button
            type="button"
            className="radar-zoom-btn"
            aria-label={target.scale > 1 ? t("radar.fit") : t("radar.whole")}
            title={target.scale > 1 ? t("radar.fit") : t("radar.whole")}
            disabled={atTarget}
            onClick={() => glideTo(target)}
          >
            <Icon name="fit" />
          </button>
        </div>
      )}
    </div>
  );
}
